import * as React from 'react';
import AggregatedSeats from './Seats/Seats';
import AggregatedBaggage from './Baggage/Baggage';
import AggregatedMeal from './Meal/Meal';
import AggregatedAnimal from './Baggage/Animal';
import AggregatedBaggageDelivery from './Baggage/BaggageDelivery';
import AggregatedAeroexpress from './Aeroexpress/Aeroexpress';
import AggregatedBusiness from './Vip/Business';
import AggregatedSms from './Vip/Sms';
import AggregatedUpgrade from './Vip/Upgrade';
import AggregatedLoyalty from './Misc/Loyalty';
import AggregatedMisc from './Misc/Misc';
import { Insurance } from '../../../../../CheckoutAdditionalService/components';
import EveryPortLounge from '../../../../../CheckoutAdditionalService/components/EveryPortLounge/Lounge/EveryPortLounge';
import { GdsAggregationServiceGroup, GdsAggregationServiceVip } from '../../../../types';
import {
	GdsServiceVipSubType,
	InsuranceProgramFragment,
	NASProductCategory,
	OrderAdditionalServiceGdsServiceServiceType
} from '@websky/graphql';
import FastTrack from '../../../../../CheckoutAdditionalService/components/EveryPortLounge/FastTrack/FastTrack';
import Vip from '../../../../../CheckoutAdditionalService/components/EveryPortLounge/Vip/Vip';
import MeetAndGreet from '../../../../../CheckoutAdditionalService/components/EveryPortLounge/MeetAndGreet/MeetAndGreet';
import EveryTravel from '../../../../../CheckoutAdditionalService/components/EveryTravel/EveryTravel';
import OrderInsuranceBlockSlotUpsale from '../../OrderInsuranceBlockSlot/OrderInsuranceBlockSlot.upsale';
import { isVipSubTypeSelected } from './utils';

interface Props {
	service: GdsAggregationServiceGroup;
	segmentId: string;
	hideCard?: boolean;
}

const ServicesFactory: React.FC<Props> = ({ service, segmentId, hideCard }) => {
	switch (service.type) {
		case OrderAdditionalServiceGdsServiceServiceType.Seat:
			return <AggregatedSeats segmentId={segmentId} service={service} hideCard={hideCard} />;

		case OrderAdditionalServiceGdsServiceServiceType.Baggage:
			return <AggregatedBaggage segmentId={segmentId} service={service} hideCard={hideCard} />;

		case OrderAdditionalServiceGdsServiceServiceType.Meal:
			return <AggregatedMeal segmentId={segmentId} service={service} hideCard={hideCard} />;

		case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
			return <AggregatedBusiness segmentId={segmentId} />;

		case OrderAdditionalServiceGdsServiceServiceType.Misc:
			return <AggregatedMisc service={service} segmentId={segmentId} hideCard={hideCard} />;

		case 'transfer':
			return <AggregatedAeroexpress segmentId={segmentId} service={service} />;

		case 'insurance':
			return (
				<Insurance insurancePrograms={service.services as InsuranceProgramFragment[]} aggregationMode={true} />
			);

		case 'insuranceGroups':
			return <OrderInsuranceBlockSlotUpsale aggregatorMode={true} segmentId={segmentId} hideCard={hideCard} />;

		case NASProductCategory.lounge:
			return <EveryPortLounge segmentId={segmentId} service={service} />;

		case NASProductCategory.fast_track:
			return <FastTrack service={service} segmentId={segmentId} />;

		case NASProductCategory.vip:
			return <Vip service={service} segmentId={segmentId} />;

		case NASProductCategory.meet_and_greet:
			return <MeetAndGreet service={service} segmentId={segmentId} />;

		case 'everyTravel':
			return <EveryTravel service={service} />;

		case 'baggageDelivery':
			return <AggregatedBaggageDelivery />;

		case 'animal':
			return <AggregatedAnimal segmentId={segmentId} />;

		case 'loyalty':
			return <AggregatedLoyalty />;

		case 'upgrade':
			return <AggregatedUpgrade segmentId={segmentId} />;

		case OrderAdditionalServiceGdsServiceServiceType.Vip: {
			const servicesMap = new Map<string, GdsAggregationServiceGroup & { subType: GdsServiceVipSubType }>();

			service.services.forEach(gdsService => {
				const vipService = gdsService as GdsAggregationServiceVip;
				const subType = vipService.subType;

				if (subType === GdsServiceVipSubType.SMS) {
					const id = vipService.id;

					servicesMap.set(id, { ...service, subType });
				} else if (subType === GdsServiceVipSubType.BusinessLounge) {
					const rfisc = vipService.rfisc;

					servicesMap.set(rfisc, {
						...service,
						services: [...(servicesMap.get(rfisc)?.services ?? []), vipService],
						subType
					});
				}
			});

			return (
				<>
					{[...servicesMap.values()].map(({ subType, ...service }) => {
						if (subType === GdsServiceVipSubType.SMS) {
							return (
								<AggregatedSms
									key="sms"
									hideCard={hideCard && isVipSubTypeSelected(service, GdsServiceVipSubType.SMS)}
								/>
							);
						} else if (subType === GdsServiceVipSubType.BusinessLounge) {
							return (
								<AggregatedBusiness
									key="business"
									service={service}
									hideCard={
										hideCard && isVipSubTypeSelected(service, GdsServiceVipSubType.BusinessLounge)
									}
									segmentId={segmentId}
								/>
							);
						}

						return null;
					})}
				</>
			);
		}

		default:
			return null;
	}
};

export default ServicesFactory;
